import { AwardIcon, ChevronRightIcon, TrophyIcon } from "lucide-react";
import { useId } from "react";
import { ContrastTeamBadge } from "@/components/app/contrast-team-badge";
import { PlayerStatsPanel, STATS_SUBCARD } from "@/components/app/player-stats-panel";
import { TeamChip } from "@/components/app/team-chip";
import { useT } from "@/i18n";
import { useTheme } from "@/lib/ground";
import { tournamentHref } from "@/lib/tournament";
import { cn } from "@/lib/utils";
import { awardPlayerStats, type PublicAward } from "../../../shared/awards";
import type { Identity } from "../../../shared/identity";
import type { RosterProfileLink } from "../../../shared/player-profile";
import type { PlayerStatRow } from "../../../shared/player-stats";
import type { PodiumRow } from "../../../shared/podium";
import type { SpiritAwardRow } from "../../../shared/spirit-awards";
import type { Division, Team } from "../../../shared/types";

/** An announced award with a name, a number and a squad against it. */
export interface AwardRow {
  award: PublicAward;
  name: string;
  number: string;
  team: { short: string; name: string; color: string } | null;
  stats?: PlayerStatRow;
}

/**
 * Put a name to each announced winner.
 *
 * The payload carries a player id and nothing else (no note, no shortlist), so
 * the name is resolved here. The CURRENT roster wins: a corrected spelling
 * should reach the board, and a snapshot's frozen copy is only the fallback.
 * An id that resolves to nobody at all (the player was removed from the roster
 * after the award was announced) is DROPPED: a public pane with an award title
 * and nobody's name against it looks broken, and the admin's awards view names
 * that problem where it can actually be fixed.
 *
 * Division order, then the order they were announced: the tab reads like the
 * results' deciding games, one division's honours together.
 */
export function resolveAwards(
  awards: PublicAward[],
  teams: Team[],
  players: PlayerStatRow[],
  divisions: Division[] = [],
  identity?: Identity,
): AwardRow[] {
  const rank = (a: PublicAward): number => {
    const i = divisions.findIndex((d) => d.id === a.divisionId);
    return i < 0 ? divisions.length : i;
  };
  const rows: AwardRow[] = [];
  for (const award of [...awards].sort(
    (x, y) => rank(x) - rank(y) || x.announcedAt - y.announcedAt,
  )) {
    const stats = awardPlayerStats(award, players, identity);
    const team = teams.find((t) => (t.roster ?? []).some((p) => p.id === award.playerId));
    const player = (team?.roster ?? []).find((p) => p.id === award.playerId);
    if (team !== undefined && player !== undefined) {
      rows.push({
        award,
        name: player.name,
        number: player.number,
        team: { short: team.short, name: team.name, color: team.color },
        ...(stats !== undefined ? { stats } : {}),
      });
      continue;
    }
    const row = stats;
    if (row !== undefined) {
      rows.push({
        award,
        name: row.name,
        number: row.number,
        stats: row,
        team:
          row.teamShort === ""
            ? null
            : { short: row.teamShort, name: row.teamName, color: row.teamColor ?? "#8a8f98" },
      });
    }
  }
  return rows;
}

/**
 * Winners share the player directory’s portrait and type treatment, with a
 * small award marker. Without a photo, the name owns the pane. Only a
 * verified public profile makes the whole pane a link.
 */
export function Awards({
  rows,
  divisions,
  profiles,
  spiritRows = [],
  podiumRows = [],
  slug,
}: {
  rows: AwardRow[];
  divisions: Division[];
  profiles: ReadonlyMap<string, RosterProfileLink>;
  spiritRows?: SpiritAwardRow[];
  podiumRows?: PodiumRow[];
  slug?: string;
}) {
  const t = useT();
  const id = useId();
  const rank = (divisionId: string): number => {
    const index = divisions.findIndex((d) => d.id === divisionId);
    return index < 0 ? divisions.length : index;
  };
  const divisionIds = [
    ...new Set([
      ...[...rows, ...spiritRows].map((row) => row.award.divisionId),
      ...podiumRows.map((row) => row.divisionId),
    ]),
  ].sort((a, b) => rank(a) - rank(b));
  return (
    <div className="space-y-10 sm:space-y-12">
      {divisionIds.map((divisionId, index) => {
        const players = rows
          .filter((row) => row.award.divisionId === divisionId)
          .sort((a, b) => a.award.announcedAt - b.award.announcedAt);
        const teams = spiritRows.filter((row) => row.award.divisionId === divisionId);
        const podium = podiumRows.filter((row) => row.divisionId === divisionId);
        const headingId = `${id}-${index}`;
        return (
          <section key={divisionId} aria-labelledby={headingId} className="space-y-4">
            <div className="flex items-center gap-4 px-1">
              <h3 id={headingId} className="nxt text-lg leading-tight sm:text-xl">
                {divisions.find((d) => d.id === divisionId)?.name ?? t("name.noDivision")}
              </h3>
              <span aria-hidden="true" className="h-px flex-1 bg-border" />
            </div>
            {podium.length > 0 && (
              <ol
                aria-label={t("directory.podiums")}
                className="grid grid-cols-2 items-end gap-3 pb-3 sm:grid-cols-3 sm:gap-4 sm:pt-2"
              >
                {podium.map((row) => (
                  <li
                    key={row.team.id}
                    value={row.place}
                    className={cn(
                      "min-w-0",
                      row.place === 1
                        ? "col-span-2 sm:order-2 sm:col-span-1 sm:col-start-2"
                        : row.place === 2
                          ? "sm:order-1"
                          : "sm:order-3",
                    )}
                  >
                    <PodiumPane row={row} slug={slug} />
                  </li>
                ))}
              </ol>
            )}
            <div className="grid items-start gap-4 adm:grid-cols-2">
              {players.length > 0 && (
                <ul className="min-w-0 space-y-4">
                  {players.map((row) => (
                    <li key={row.award.id}>
                      <AwardPane row={row} profile={profiles.get(row.award.playerId)} />
                    </li>
                  ))}
                </ul>
              )}
              {teams.length > 0 && (
                <ul className="min-w-0 space-y-4">
                  {teams.map((row) => (
                    <li key={row.team.id}>
                      <SpiritAwardPane row={row} slug={slug} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function PodiumPane({ row, slug }: { row: PodiumRow; slug: string | undefined }) {
  const t = useT();
  const theme = useTheme();
  const { team, place } = row;
  const champion = place === 1;
  const darkSurface = champion || theme === "dark";
  const logo = darkSurface ? (team.logoDark ?? team.logo) : team.logo;
  const className = cn(
    "group relative isolate flex flex-col items-center overflow-hidden rounded-3xl border px-4 pb-6 pt-5 text-center focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring sm:px-6",
    champion
      ? "z-20 min-h-72 border-band-border bg-band text-band-foreground shadow-[0_28px_55px_-12px] shadow-band/35 sm:min-h-80 dark:shadow-band/90"
      : "min-h-56 border-border bg-card text-card-foreground sm:min-h-64",
    place === 2 && "z-10 shadow-[0_14px_28px_-12px] shadow-band/20 dark:shadow-band/65",
    place === 3 && "sm:min-h-60",
  );
  const content = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute -right-1 -bottom-6 -z-10 nxt tabular text-[9rem] leading-none sm:text-[11rem]",
          champion ? "text-band-foreground/10" : "text-foreground/5",
        )}
      >
        {place}
      </span>
      <div
        className={cn(
          "mb-5 flex items-center justify-center gap-2",
          champion ? "text-brand" : "text-muted-foreground",
        )}
      >
        {champion ? <TrophyIcon aria-hidden="true" className="size-5" /> : null}
        <span className="text-xs font-medium">
          {t(champion ? "board.award.champion" : "directory.finished", { place })}
        </span>
      </div>
      <div className="flex flex-1 flex-col items-center justify-center gap-5">
        <div
          className={cn(
            "flex shrink-0 items-center justify-center motion-safe:transition-transform motion-safe:duration-200 motion-safe:group-hover:-translate-y-1",
            champion ? "size-24 sm:size-28" : "size-16 sm:size-20",
          )}
        >
          <ContrastTeamBadge
            logo={logo}
            surface={champion ? "band" : "card"}
            color={team.color}
            short={team.short}
            className={cn(
              champion ? "size-full text-3xl sm:text-4xl" : "size-full text-xl sm:text-2xl",
              champion &&
                team.logoDark === undefined &&
                team.logo === undefined &&
                "outline outline-band-foreground/30",
            )}
          />
        </div>
        <h4
          className={cn(
            "nxt w-full leading-tight [overflow-wrap:anywhere]",
            champion ? "text-2xl sm:text-3xl" : "text-base sm:text-xl",
          )}
        >
          {team.name}
        </h4>
      </div>
      {slug !== undefined && (
        <ChevronRightIcon
          aria-hidden="true"
          className={cn(
            "absolute right-3 bottom-3 size-4",
            champion ? "text-band-muted" : "text-muted-foreground/60",
          )}
        />
      )}
    </>
  );
  return slug !== undefined ? (
    <a href={tournamentHref(slug, `/teams/${encodeURIComponent(team.id)}`)} className={className}>
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
}

function SpiritAwardPane({ row, slug }: { row: SpiritAwardRow; slug: string | undefined }) {
  const t = useT();
  const theme = useTheme();
  const { team } = row;
  const className =
    "board-float group relative z-10 flex min-h-48 items-center gap-5 overflow-hidden bg-card p-6 transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring";
  const content = (
    <>
      <ContrastTeamBadge
        logo={theme === "dark" ? (team.logoDark ?? team.logo) : team.logo}
        color={team.color}
        short={team.short}
        className="size-20 text-xl sm:size-24 sm:text-2xl"
      />
      <div className="min-w-0 flex-1 space-y-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="inline-flex items-center gap-1.5 text-brand-ink dark:text-brand">
            <AwardIcon aria-hidden="true" className="size-4 shrink-0" />
            <span className="nxt text-sm">SOTG</span>
          </span>
        </div>
        <h4 className="nxt text-xl leading-tight [overflow-wrap:anywhere] sm:text-2xl">
          {team.name}
        </h4>
        <p className="text-sm text-muted-foreground">
          {t(row.shared ? "board.award.sharedSpirit" : "board.award.spirit")}
        </p>
      </div>
      {slug !== undefined && (
        <ChevronRightIcon
          aria-hidden="true"
          className="absolute right-2 bottom-3 size-4 text-muted-foreground/60 group-hover:text-foreground"
        />
      )}
    </>
  );
  return (
    <div className="relative isolate">
      {slug !== undefined ? (
        <a
          href={tournamentHref(slug, `/teams/${encodeURIComponent(team.id)}`)}
          className={className}
        >
          {content}
        </a>
      ) : (
        <div className={className}>{content}</div>
      )}
      {row.average !== undefined && (
        <dl className={cn(STATS_SUBCARD, "text-center")}>
          <div className="flex items-center justify-center gap-3">
            <dt className="order-2 text-xs whitespace-nowrap text-muted-foreground">
              {t("board.award.spiritAverage")}
            </dt>
            <dd className="nxt tabular text-xl leading-none sm:text-2xl">
              {row.average.toFixed(2)}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function AwardPane({ row, profile }: { row: AwardRow; profile: RosterProfileLink | undefined }) {
  const photo = profile?.photo;
  const className = cn(
    "board-float group relative z-10 isolate flex min-h-48 overflow-hidden bg-card",
    profile !== undefined &&
      "transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring",
  );
  const content = (
    <>
      {photo !== undefined && (
        <img
          src={photo}
          alt=""
          loading="lazy"
          className="pointer-events-none absolute inset-y-0 left-0 h-full w-[48%] object-cover"
          style={{
            objectPosition: `center ${(profile?.photoFocus ?? 0.5) * 100}%`,
            maskImage:
              "linear-gradient(108deg, #000 48%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,0.5) 74%, rgba(0,0,0,0.15) 88%, transparent 100%), linear-gradient(to right, #000 76%, transparent 100%)",
            maskComposite: "intersect",
            WebkitMaskImage:
              "linear-gradient(108deg, #000 48%, rgba(0,0,0,0.9) 60%, rgba(0,0,0,0.5) 74%, rgba(0,0,0,0.15) 88%, transparent 100%), linear-gradient(to right, #000 76%, transparent 100%)",
            WebkitMaskComposite: "source-in",
          }}
        />
      )}
      <div
        className={cn(
          "@container relative flex min-w-0 flex-1 flex-col justify-center gap-4 py-6 pr-6",
          photo !== undefined ? "pl-[46%]" : "pl-6",
        )}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="inline-flex min-w-0 items-center gap-1.5 text-brand-ink dark:text-brand">
            <AwardIcon aria-hidden="true" className="size-4 shrink-0" />
            <span className="nxt text-sm leading-tight [overflow-wrap:anywhere]">
              {row.award.title.toUpperCase()}
            </span>
          </span>
        </div>
        <h4 className="nxt text-[clamp(0.875rem,10.5cqw,1.75rem)] leading-tight [overflow-wrap:anywhere]">
          {row.name}
        </h4>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          {row.team !== null && (
            <>
              <TeamChip short={row.team.short} color={row.team.color} />
              <span className="min-w-0 [overflow-wrap:anywhere]">{row.team.name}</span>
            </>
          )}
          {row.number !== "" && <span className="tabular text-xs">#{row.number}</span>}
        </div>
      </div>
      {profile !== undefined && (
        <ChevronRightIcon
          aria-hidden="true"
          className="absolute right-2 bottom-3 size-4 text-muted-foreground/60 group-hover:text-foreground"
        />
      )}
    </>
  );
  return (
    <div className="relative isolate">
      {profile !== undefined ? (
        <a href={`/players/${encodeURIComponent(profile.accountId)}`} className={className}>
          {content}
        </a>
      ) : (
        <div className={className}>{content}</div>
      )}
      {row.stats !== undefined && <PlayerStatsPanel name={row.name} stats={row.stats} />}
    </div>
  );
}
